import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getAuditQueueUseCases,
  getAuditQueueTasks,
  getAuditQueueResults,
  setCurrentUser,
  AuditQueueUseCase,
  AuditQueueTask,
  AuditQueueResult,
  AuditQueueUser,
} from "src/store/auditQueueSlice";

export interface UseAuditQueueReturn {
  useCases: AuditQueueUseCase[];
  tasks: AuditQueueTask[];
  results: AuditQueueResult[];
  currentUser: AuditQueueUser;
  isLoading: boolean;
  isError: boolean;
  useCaseById: (id: string) => AuditQueueUseCase | undefined;
  resultsForTask: (taskId: string) => AuditQueueResult[];
  resultForCurrentUser: (taskId: string) => AuditQueueResult | undefined;
}

export function useAuditQueue(): UseAuditQueueReturn {
  const dispatch = useAppDispatch();

  const {
    useCases,
    tasks,
    results,
    currentUser,
    getUseCasesLoading,
    getTasksLoading,
    getResultsLoading,
    tasksLoaded,
    resultsLoaded,
  } = useAppSelector((state) => state.auditQueueSlice);

  // Read the logged-in user from the existing userSlice and push into auditQueueSlice.
  const { alias, userLoading } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (alias && alias !== currentUser.login) {
      dispatch(
        setCurrentUser({
          login: alias,
          name: alias,
          role: "auditor",
          isElevated: false,
        }),
      );
    }
  }, [alias, currentUser.login, dispatch]);

  // Use cases change rarely: fetch them once.
  useEffect(() => {
    if (alias && getUseCasesLoading === "idle") dispatch(getAuditQueueUseCases());
  }, [dispatch, alias, getUseCasesLoading]);

  // Tasks and results are reloaded every time the queue opens, so a task submitted
  // or saved in the workspace shows up under the right tab on return.
  useEffect(() => {
    if (!alias) return;
    dispatch(getAuditQueueTasks(alias))
      .unwrap()
      .then((loaded) =>
        dispatch(getAuditQueueResults({ taskIds: loaded.map((t) => t.id), currentAlias: alias })),
      )
      .catch(() => undefined); // the rejected state already drives isError
  }, [dispatch, alias]);

  // Treat "idle" as loading too — thunks dispatch in useEffect (after first
  // render), so without this the component briefly flashes the empty state.
  // Once the user slice has resolved and alias is still empty, stop waiting
  // so we do not show a spinner forever.
  const aliasResolved = userLoading === "fulfilled" || userLoading === "rejected";
  // Only the first load shows the spinner; a refresh keeps the current list on
  // screen until the new one arrives.
  const isLoading = !aliasResolved
    ? true
    : getUseCasesLoading === "idle" ||
      getUseCasesLoading === "pending" ||
      (!tasksLoaded && getTasksLoading !== "rejected") ||
      (!resultsLoaded && getResultsLoading !== "rejected" && getTasksLoading !== "rejected");

  // A failed refresh keeps the loaded list; only a failed first load is an error page.
  const isError =
    (aliasResolved && !alias) ||
    getUseCasesLoading === "rejected" ||
    (!tasksLoaded && getTasksLoading === "rejected") ||
    (!resultsLoaded && getResultsLoading === "rejected");

  // Selectors (memoised so child components see stable references).
  const useCaseById = useCallback((id: string) => useCases.find((u) => u.id === id), [useCases]);

  const resultsForTask = useCallback(
    (taskId: string) => results.filter((r) => r.task_id === taskId),
    [results],
  );

  const resultForCurrentUser = useCallback(
    (taskId: string) =>
      results.find((r) => r.task_id === taskId && r.auditor_id === currentUser.login),
    [results, currentUser.login],
  );

  return {
    useCases,
    tasks,
    results,
    currentUser,
    isLoading,
    isError,
    useCaseById,
    resultsForTask,
    resultForCurrentUser,
  };
}
